import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardMedia,
  CardContent,
} from '@mui/material';
import {
  Download,
  Delete,
  Visibility,
  Search,
  AttachFile,
  FilterList,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import type { Receipt, Filters } from '../types';
import { useAuth } from '../contexts/AuthContext';
import {
  PageLayout,
  ScrollableContent,
  PageHeader,
  StandardPagination,
  ResponsiveTable,
  FilterChip
} from '../components/Layout/index';
import { 
  Button, 
  Chip, 
  DataTable,
  ActionButtons
} from '../design-system';
import GenericFilterModal, { FilterField } from '../components/GenericFilterModal';
import { useFilterState } from '../hooks/useFilterState';

const Receipts: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Stati locali per i filtri (senza debounce)
  const [localFilters, setLocalFilters] = useState({
    search: '',
    mimeType: '',
    dateFrom: '',
    dateTo: '',
  });
  
  // Stati per la query (con debounce)
  const [queryFilters, setQueryFilters] = useState({
    search: '',
    mimeType: '',
    dateFrom: '',
    dateTo: '',
  });
  
  const [selectedReceipt, setSelectedReceipt] = useState<Receipt | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  const { hasAnyRole } = useAuth();
  const queryClient = useQueryClient();

  // Hook per gestire lo stato dei filtri
  const { hasActiveFilters, activeFiltersCount } = useFilterState(queryFilters);

  // Configurazione campi per la modale filtri
  const filterFields: FilterField[] = [
    {
      key: 'search',
      label: 'Cerca (Donatore, Nome File)',
      type: 'text',
      icon: <Search />,
      size: { xs: 12, sm: 6 },
    },
    {
      key: 'mimeType',
      label: 'Tipo File',
      type: 'select',
      options: [
        { value: '', label: 'Tutti' },
        { value: 'application/pdf', label: 'PDF' },
        { value: 'image/', label: 'Immagini' },
      ],
      size: { xs: 12, sm: 6 },
    },
    {
      key: 'dateFrom',
      label: 'Data da',
      type: 'date',
      size: { xs: 12, sm: 6 },
    },
    {
      key: 'dateTo',
      label: 'Data a',
      type: 'date',
      size: { xs: 12, sm: 6 },
    },
  ];

  // Valori correnti dei filtri
  const filterValues = {
    search: localFilters.search,
    mimeType: localFilters.mimeType,
    dateFrom: localFilters.dateFrom,
    dateTo: localFilters.dateTo,
  };

  // Funzioni per aggiornare i filtri
  const updateFilter = useCallback((key: string, value: any) => {
    setLocalFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const applyFilters = useCallback(() => {
    setQueryFilters(localFilters);
  }, [localFilters]);

  const clearFilters = useCallback(() => {
    const emptyFilters = {
      search: '',
      mimeType: '',
      dateFrom: '',
      dateTo: '',
    };
    setLocalFilters(emptyFilters);
    setQueryFilters(emptyFilters);
    setPage(0);
  }, []);

  const filters: Filters = useMemo(() => ({
    page: page + 1,
    limit: rowsPerPage,
    search: queryFilters.search || undefined,
  }), [page, rowsPerPage, queryFilters]);

  // Per ora mostriamo le ricevute tramite le donazioni
  const { data: donations, isLoading, error } = useQuery({
    queryKey: ['donations', { includeReceipts: true }],
    queryFn: () => apiService.getDonations({ limit: 100 }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiService.deleteReceipt(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['donations'] });
      setOpenDialog(false);
      setSelectedReceipt(null);
    },
  });

  const handleViewReceipt = async (receipt: Receipt) => {
    setSelectedReceipt(receipt);
    setOpenDialog(true);
    
    try {
      // Per l'anteprima, usiamo l'endpoint /file che restituisce il PDF direttamente
      const blob = await apiService.getReceiptFileBlob(receipt.id);
      
      // Creiamo un blob URL per l'iframe
      const blobUrl = URL.createObjectURL(blob);
      setDownloadUrl(blobUrl);
    } catch (error) {
      console.error('Failed to get receipt file:', error);
      setDownloadUrl(null);
    }
  };

  const handleDownload = async (receipt: Receipt) => {
    try {
      await apiService.downloadReceiptFile(receipt.id, receipt.originalName);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Sei sicuro di voler eliminare questa ricevuta?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    // Validazione per assicurarsi che la pagina non sia negativa
    if (newPage >= 0) {
      setPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    if (newRowsPerPage > 0) {
      setRowsPerPage(newRowsPerPage);
      setPage(0);
    }
  };

  // Validazione pagina corrente quando cambia rowsPerPage
  useEffect(() => {
    if (donations?.data && donations.data.length > 0) {
      const maxPage = Math.max(0, Math.ceil(donations.data.length / rowsPerPage) - 1);
      if (page > maxPage) {
        setPage(maxPage);
      }
    }
  }, [donations?.data, rowsPerPage, page]);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileTypeIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) {
      return '🖼️';
    } else if (mimeType === 'application/pdf') {
      return '📄';
    }
    return '📎';
  };

  // Filtra le donazioni che hanno ricevute e applica i filtri
  const filteredDonations = useMemo(() => {
    if (!donations?.data) return [];
    
    let filtered = donations.data.filter(donation => 
      donation.receipts && donation.receipts.length > 0
    );

    // Applica filtri
    if (queryFilters.search) {
      const searchLower = queryFilters.search.toLowerCase();
      filtered = filtered.filter(donation => 
        donation.receipts?.some(receipt => 
          receipt.originalName.toLowerCase().includes(searchLower) ||
          (donation.donor && 
            `${donation.donor.firstName} ${donation.donor.lastName}`.toLowerCase().includes(searchLower))
        )
      );
    }

    if (queryFilters.mimeType) {
      filtered = filtered.filter(donation => 
        donation.receipts?.some(receipt => 
          receipt.mimeType.startsWith(queryFilters.mimeType)
        )
      );
    }

    if (queryFilters.dateFrom) {
      const fromDate = new Date(queryFilters.dateFrom);
      filtered = filtered.filter(donation => 
        donation.receipts?.some(receipt => 
          new Date(receipt.createdAt) >= fromDate
        )
      );
    }

    if (queryFilters.dateTo) {
      const toDate = new Date(queryFilters.dateTo);
      toDate.setHours(23, 59, 59, 999); // Fine giornata
      filtered = filtered.filter(donation => 
        donation.receipts?.some(receipt => 
          new Date(receipt.createdAt) <= toDate
        )
      );
    }

    return filtered;
  }, [donations?.data, queryFilters]);

  // Estrai tutte le ricevute dalle donazioni filtrate
  const allReceipts = useMemo(() => {
    const receipts: Array<Receipt & { donation: any }> = [];
    filteredDonations.forEach(donation => {
      donation.receipts?.forEach(receipt => {
        receipts.push({ ...receipt, donation });
      });
    });
    return receipts;
  }, [filteredDonations]);

  // Paginazione delle ricevute
  const paginatedReceipts = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return allReceipts.slice(start, end);
  }, [allReceipts, page, rowsPerPage]);

  // Definizione colonne per ResponsiveTable
  const columns = [
    {
      id: 'file',
      key: 'file',
      label: 'File',
      render: (receipt: Receipt & { donation: any }) => (
        <Box display="flex" alignItems="center">
          <Typography sx={{ mr: 1, fontSize: '1.2rem' }}>
            {getFileTypeIcon(receipt.mimeType)}
          </Typography>
          <Typography 
            variant="body2" 
            fontWeight="bold"
            sx={{ color: 'text.primary' }}
          >
            {receipt.originalName}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'receipt',
      key: 'receipt',
      label: 'Ricevuta',
      render: (receipt: Receipt & { donation: any }) => (
        <Typography 
          variant="body2"
          sx={{ color: 'text.primary', fontWeight: 500 }}
        >
          {receipt.donation.reference || receipt.donation.id.slice(0, 8)}
        </Typography>
      ),
    },
    {
      id: 'donation',
      key: 'donation',
      label: 'Donazione',
      render: (receipt: Receipt & { donation: any }) => (
        <Typography 
          variant="body2"
          sx={{ color: 'success.main', fontWeight: 600 }}
        >
          €{Number(receipt.donation.amount).toFixed(2)}
        </Typography>
      ),
    },
    {
      id: 'donor',
      key: 'donor',
      label: 'Donatore',
      render: (receipt: Receipt & { donation: any }) => (
        <Typography 
          variant="body2"
          sx={{ color: 'text.primary' }}
        >
          {receipt.donation.donor ? 
            `${receipt.donation.donor.firstName} ${receipt.donation.donor.lastName}` : 
            'N/A'
          }
        </Typography>
      ),
    },
    {
      id: 'date',
      key: 'date',
      label: 'Data Upload',
      render: (receipt: Receipt & { donation: any }) => (
        <Typography 
          variant="body2"
          sx={{ color: 'text.secondary' }}
        >
          {new Date(receipt.createdAt).toLocaleDateString('it-IT')}
        </Typography>
      ),
    },
    {
      id: 'actions',
      key: 'actions',
      label: 'Azioni',
      align: 'right' as const,
      render: (receipt: Receipt & { donation: any }) => (
        <ActionButtons
          onView={() => handleViewReceipt(receipt)}
          onEdit={() => handleDownload(receipt)}
          onDelete={() => handleDelete(receipt.id)}
          showEdit={true}
          showDelete={hasAnyRole(['ADMIN', 'SUPERVISOR'])}
          editTooltip="Scarica"
          editIcon="download"
          size="small"
          spacing={0.5}
        />
      ),
    },
  ];

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Caricamento ricevute...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography color="error">Errore nel caricamento delle ricevute</Typography>
      </Box>
    );
  }

  return (
    <PageLayout>
      <PageHeader>
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
          <Typography variant="h4" component="h1">
            Ricevute
          </Typography>
          <Box display="flex" gap={2} alignItems="center">
            <FilterChip
              hasActiveFilters={hasActiveFilters}
              activeFiltersCount={activeFiltersCount}
              onClick={() => setFilterModalOpen(true)}
            />
          </Box>
        </Box>
      </PageHeader>

      <ScrollableContent maxHeight="calc(100vh - 400px)">
                <ResponsiveTable
                  data={paginatedReceipts}
                  columns={columns as any}
                  maxHeight="100%"
                  stickyHeader={true}
                  loading={isLoading}
                />
      </ScrollableContent>

      <StandardPagination
        count={allReceipts.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <Dialog 
        open={openDialog} 
        onClose={() => {
          // Pulisci il blob URL per evitare memory leaks
          if (downloadUrl && downloadUrl.startsWith('blob:')) {
            URL.revokeObjectURL(downloadUrl);
          }
          setOpenDialog(false);
          setSelectedReceipt(null);
          setDownloadUrl(null);
        }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          Dettagli Ricevuta
        </DialogTitle>
        <DialogContent>
          {selectedReceipt && (
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2}>
              <Box flex={1}>
                <Card sx={{ backgroundColor: 'background.paper' }}>
                  <CardContent>
                    <Typography 
                      variant="h6" 
                      gutterBottom
                      sx={{ color: 'text.primary', fontWeight: 600 }}
                    >
                      Informazioni Ricevuta
                    </Typography>
                    <Box sx={{ '& > *': { mb: 2 } }}>
                      {/* Numero Ricevuta */}
                      <Typography variant="body2">
                        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          Numero Ricevuta:
                        </Box>{' '}
                        <Box component="span" sx={{ color: 'primary.main', fontWeight: 500 }}>
                          {(selectedReceipt as any).donation?.reference || (selectedReceipt as any).donation?.id?.slice(0, 8)}
                        </Box>
                      </Typography>
                      
                      {/* Nome Donatore */}
                      <Typography variant="body2">
                        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          Donatore:
                        </Box>{' '}
                        <Box component="span" sx={{ color: 'text.secondary' }}>
                          {(selectedReceipt as any).donation?.donor ? 
                            `${(selectedReceipt as any).donation.donor.firstName} ${(selectedReceipt as any).donation.donor.lastName}` : 
                            'N/A'
                          }
                        </Box>
                      </Typography>
                      
                      {/* Importo */}
                      <Typography variant="body2">
                        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          Importo:
                        </Box>{' '}
                        <Box component="span" sx={{ color: 'success.main', fontWeight: 600, fontSize: '1.1rem' }}>
                          €{(selectedReceipt as any).donation?.amount ? Number((selectedReceipt as any).donation.amount).toFixed(2) : 'N/A'}
                        </Box>
                      </Typography>
                      
                      {/* Separatore */}
                      <Box sx={{ borderTop: 1, borderColor: 'divider', my: 2 }} />
                      
                      {/* Informazioni File */}
                      <Typography variant="body2">
                        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          Nome file:
                        </Box>{' '}
                        <Box component="span" sx={{ color: 'text.secondary' }}>
                          {selectedReceipt.originalName}
                        </Box>
                      </Typography>
                      
                      <Typography variant="body2">
                        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          Tipo MIME:
                        </Box>{' '}
                        <Box component="span" sx={{ color: 'text.secondary' }}>
                          {selectedReceipt.mimeType}
                        </Box>
                      </Typography>
                      
                      <Typography variant="body2">
                        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          Dimensione:
                        </Box>{' '}
                        <Box component="span" sx={{ color: 'text.secondary' }}>
                          {formatFileSize(selectedReceipt.fileSize)}
                        </Box>
                      </Typography>
                      
                      <Typography variant="body2">
                        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          Data upload:
                        </Box>{' '}
                        <Box component="span" sx={{ color: 'text.secondary' }}>
                          {new Date(selectedReceipt.createdAt).toLocaleString('it-IT')}
                        </Box>
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
              <Box flex={1}>
                {downloadUrl && selectedReceipt.mimeType.startsWith('image/') && (
                  <Card sx={{ backgroundColor: 'background.paper' }}>
                    <CardMedia
                      component="img"
                      height="300"
                      image={downloadUrl}
                      alt={selectedReceipt.originalName}
                      sx={{ objectFit: 'contain' }}
                    />
                  </Card>
                )}
                {selectedReceipt.mimeType === 'application/pdf' && (
                  <Card sx={{ backgroundColor: 'background.paper' }}>
                    <CardContent>
                      <Typography 
                        variant="h6" 
                        gutterBottom
                        sx={{ color: 'text.primary', fontWeight: 600, mb: 2 }}
                      >
                        Anteprima PDF
                      </Typography>
                      {downloadUrl ? (
                        <Box 
                          sx={{ 
                            width: '100%', 
                            height: '400px',
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                            overflow: 'hidden'
                          }}
                        >
                          <iframe
                            src={downloadUrl}
                            width="100%"
                            height="100%"
                            style={{ border: 'none' }}
                            title={`Anteprima ${selectedReceipt.originalName}`}
                          />
                        </Box>
                      ) : (
                        <Box display="flex" flexDirection="column" alignItems="center" p={4}>
                          <AttachFile 
                            sx={{ 
                              fontSize: 64, 
                              color: 'primary.main',
                              mb: 2
                            }} 
                          />
                          <Typography 
                            variant="h6" 
                            gutterBottom
                            sx={{ color: 'text.primary', fontWeight: 600 }}
                          >
                            File PDF
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ color: 'text.secondary', textAlign: 'center' }}
                          >
                            Clicca su "Scarica" per visualizzare il file
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              // Pulisci il blob URL per evitare memory leaks
              if (downloadUrl && downloadUrl.startsWith('blob:')) {
                URL.revokeObjectURL(downloadUrl);
              }
              setOpenDialog(false);
              setSelectedReceipt(null);
              setDownloadUrl(null);
            }}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'grey.100',
                color: 'text.primary',
              },
            }}
          >
            Chiudi
          </Button>
          {selectedReceipt && (
            <Button
              variant="primary"
              startIcon={<Download />}
              onClick={() => handleDownload(selectedReceipt)}
              sx={{
                backgroundColor: 'success.main',
                color: 'success.contrastText',
                '&:hover': {
                  backgroundColor: 'success.dark',
                },
              }}
            >
              Scarica
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* GenericFilterModal */}
      <GenericFilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        title="Filtri Ricevute"
        fields={filterFields}
        values={filterValues}
        onUpdateValue={updateFilter}
        onApply={() => {
          setPage(0);
          applyFilters();
        }}
        onClear={clearFilters}
      />
    </PageLayout>
  );
};

export default Receipts;
